import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../contexts/AuthContext";
import { useSalePage } from "../../contexts/SalePageContext";
import { Helmet } from "react-helmet-async";
import { signOut } from "firebase/auth";
import { db, auth } from "../../lib/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, getDoc, collection, query, where, getDocs, orderBy } from "firebase/firestore";
import {
  LayoutDashboard, Sparkles, ChevronDown, Crown, User, Settings, Package, LayoutGrid,
  Check, Info, ArrowRight, MessageSquare, CreditCard, Receipt, RefreshCw, LogOut,
  Calendar, X, Loader, Wrench,
} from "lucide-react";

const ADMIN_EMAIL = "viitamint@gmail.com";

const FONT = '"IBM Plex Sans Thai", system-ui, sans-serif';
const HEAD = "'Anuphan', sans-serif";
const INK = "#0E3A40";

const PLAN_META = {
  basic: {
    name: "Basic",
    price: 149,
    desc: "สำหรับผู้เริ่มต้น",
    base: ["วิดีโอ 3 คลิป · รูปภาพ/ข้อความไม่จำกัด", "ปุ่ม CTA · ราคาสินค้า"],
    more: ["YouTube ไม่จำกัด", "รีวิวลูกค้า 3 รายการ · FAQ 5 คำถาม"],
  },
  premium: {
    name: "Premium",
    price: 249,
    desc: "ฟีเจอร์ครบครัน สำหรับมืออาชีพ",
    base: ["วิดีโอ 6 คลิป · ระบบชำระเงิน", "รีวิว/FAQ ไม่จำกัด · แกลเลอรี่"],
    more: ["นับถอยหลัง · ตารางเปรียบเทียบ", "ฟอร์มติดต่อ · White Label"],
  },
  premium_vip: {
    name: "Premium VIP",
    price: 499,
    desc: "ทุกอย่างใน Premium + สร้างหน้าด้วย AI",
    base: ["ทุกฟีเจอร์ใน Premium ครบ", "สร้าง Sale Page ด้วย AI (10 ครั้ง/เดือน)"],
    more: ["AI จัดหน้า+เขียนคำโฆษณาให้อัตโนมัติ", "แก้ไขต่อได้อิสระหลังสร้าง"],
  },
} as const;

function planLabel(plan?: string) {
  if (!plan) return "สมาชิก";
  if (plan === "free") return "ทดสอบระบบ";
  if (plan === "premium_vip") return "Premium VIP";
  return plan.toUpperCase();
}

export default function SalePageRenew() {
  const { user } = useAuthContext();
  const { subscription, loading: salePageLoading, refreshSubscription } = useSalePage();
  const navigate = useNavigate();
  const isAdmin = user?.email === ADMIN_EMAIL;
  const [adminBusy, setAdminBusy] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<"basic" | "premium" | "premium_vip">("basic");
  const [basicExpanded, setBasicExpanded] = useState(false);
  const [premiumExpanded, setPremiumExpanded] = useState(false);
  const [vipExpanded, setVipExpanded] = useState(false);
  const [lmsSubscriptionEndDate, setLmsSubscriptionEndDate] = useState<Date | null>(null);
  const isProPlusYearly = false; // Pro+ Yearly removed from Sale Page
  const [showHistory, setShowHistory] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);

  // header dropdowns
  const [featOpen, setFeatOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const featRef = useRef<HTMLDivElement>(null);
  const acctRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (featRef.current && !featRef.current.contains(t)) setFeatOpen(false);
      if (acctRef.current && !acctRef.current.contains(t)) setAcctOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    const fetchLmsSubscription = async () => {
      try {
        setSelectedPlan("basic");
        const paymentsQuery = query(
          collection(db, "salePagePaymentNotifications"),
          where("userId", "==", user.uid),
          where("status", "in", ["verified", "approved"]),
          orderBy("createdAt", "desc")
        );
        const paymentsSnap = await getDocs(paymentsQuery);
        const payments = paymentsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          paidAt: d.data().verifiedAt?.toDate() || d.data().createdAt?.toDate(),
          amount: d.data().amount,
          plan: d.data().plan,
          source: "payment",
        }));
        const subDoc = await getDoc(doc(db, "salePageSubscriptions", user.uid));
        const renewalHistory: any[] = [];
        if (subDoc.exists()) {
          const subData = subDoc.data();
          if (subData.lastPaymentDate) {
            renewalHistory.push({
              id: "renewal-" + subData.lastPaymentDate.toMillis(),
              paidAt: subData.lastPaymentDate.toDate(),
              amount: subData.lastPaymentAmount || 0,
              plan: subData.plan,
              source: "renewal",
            });
          }
          if (subData.updatedAt && subData.createdAt &&
              subData.updatedAt.toMillis() !== subData.createdAt.toMillis() &&
              !subData.lastPaymentDate) {
            renewalHistory.push({
              id: "renewal-" + subData.updatedAt.toMillis(),
              paidAt: subData.updatedAt.toDate(),
              amount: 0,
              plan: subData.plan,
              source: "renewal",
            });
          }
        }
        const allHistory = [...payments, ...renewalHistory].sort(
          (a, b) => (b.paidAt?.getTime() || 0) - (a.paidAt?.getTime() || 0)
        );
        setPaymentHistory(allHistory);
      } catch (error) {
        console.error("Error fetching LMS subscription:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLmsSubscription();
  }, [user, navigate]);

  const formatDate = (date: any) => {
    if (!date) return "-";
    const d = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString("th-TH", { year: "numeric", month: "long", day: "numeric" });
  };
  const getDaysUntilExpiry = (expiryDate: any) => {
    if (!expiryDate) return null;
    const expiry = expiryDate instanceof Date ? expiryDate : expiryDate.toDate ? expiryDate.toDate() : new Date(expiryDate);
    const diff = Math.ceil((expiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const currentPlan = subscription?.plan || "none";

  let displayEndDate: Date | null = null;
  if (currentPlan === "none" || currentPlan === "free" || !subscription) {
    if (subscription?.trialEndDate) displayEndDate = subscription.trialEndDate.toDate();
    else if (user?.createdAt) {
      const createdDate = user.createdAt instanceof Date ? user.createdAt : (user.createdAt as any).toDate();
      const trialEnd = new Date(createdDate);
      trialEnd.setDate(trialEnd.getDate() + 7);
      displayEndDate = trialEnd;
    }
  } else if (isProPlusYearly && lmsSubscriptionEndDate) {
    displayEndDate = lmsSubscriptionEndDate;
  } else if (subscription?.status === "trial" && subscription?.trialEndDate) {
    displayEndDate = subscription.trialEndDate.toDate();
  } else if (subscription?.status === "active" && subscription?.subscriptionEndDate) {
    displayEndDate = subscription.subscriptionEndDate.toDate();
  } else if (subscription?.status === "expired" && subscription?.trialEndDate) {
    displayEndDate = subscription.trialEndDate.toDate();
  }
  const daysLeft = displayEndDate ? getDaysUntilExpiry(displayEndDate) : null;

  // free-now: Pro+ Yearly users get Basic free during their entitlement
  const freeNow = isProPlusYearly && selectedPlan === "basic";

  const handleConfirm = () => {
    if (freeNow) {
      navigate("/dashboard");
      return;
    }
    const meta = PLAN_META[selectedPlan];
    const price = meta.price;
    const checkoutData = {
      plan: selectedPlan,
      planName: meta.name,
      price,
      billingPeriod: "monthly",
      isRenewal: currentPlan === selectedPlan,
      isUpgrade: currentPlan !== selectedPlan,
      source: "salepage",
    };
    localStorage.setItem("salePageCheckout", JSON.stringify(checkoutData));
    navigate("/salepage/checkout-slip");
  };

  const adminSetPlan = async (plan: "test" | "basic" | "premium" | "premium_vip", billingPeriod?: "monthly" | "yearly") => {
    const key = billingPeriod ? `${plan}_${billingPeriod}` : plan;
    setAdminBusy(key);
    try {
      const fn = httpsCallable(getFunctions(undefined, "us-central1"), "adminSetOwnSalepagePlan");
      await fn({ plan, billingPeriod });
      await refreshSubscription();
    } catch (e) {
      console.error("[adminSetPlan] error:", e);
      alert("ตั้งค่าไม่สำเร็จ กรุณาลองใหม่");
    } finally {
      setAdminBusy(null);
    }
  };

  const doLogout = async () => {
    try { localStorage.removeItem("auth_user_cache"); await signOut(auth); } catch { /* ignore */ }
    navigate("/");
  };

  const acctName: string =
    (user as any)?.displayName || (user as any)?.name || (user as any)?.username ||
    (user?.email || "").split("@")[0] || "บัญชีของฉัน";

  // current plan card content
  const curLabel = isProPlusYearly
    ? "Basic รายปี"
    : currentPlan === "premium_vip" ? "Premium VIP"
    : currentPlan === "premium" ? "Premium"
    : currentPlan === "basic" ? "Basic"
    : "FREE";
  const curSub = isProPlusYearly
    ? `ใช้งานฟรีถึง ${formatDate(displayEndDate)}${daysLeft != null ? ` · เหลืออีก ${daysLeft.toLocaleString()} วัน` : ""}`
    : displayEndDate
    ? `${currentPlan === "none" || currentPlan === "free" ? "ทดลองใช้ถึง" : "ใช้งานถึง"} ${formatDate(displayEndDate)}${daysLeft != null ? ` · ${daysLeft <= 0 ? `หมดอายุแล้ว ${Math.abs(daysLeft)} วัน` : `เหลืออีก ${daysLeft.toLocaleString()} วัน`}` : ""}`
    : "จัดการแพ็คเกจของคุณได้ที่นี่";

  const hasValidSub = !!subscription && (subscription.status === "active" || subscription.status === "trial");
  const featureItems: { to: string; label: string; icon: JSX.Element; show?: boolean }[] = [
    { to: "/salepage/messages", label: "ข้อความ", icon: <MessageSquare style={{ width: 16, height: 16, color: "#3A93B9" }} />, show: hasValidSub },
    { to: "/salepage/payments", label: "การชำระเงิน", icon: <CreditCard style={{ width: 16, height: 16, color: "#00B4A1" }} />, show: hasValidSub },
    { to: "/salepage/payment-history", label: "ประวัติการชำระ", icon: <Receipt style={{ width: 16, height: 16, color: "#5B6FCB" }} /> },
    { to: "/salepage/settings", label: "ตั้งค่า", icon: <Settings style={{ width: 16, height: 16, color: "#5B7D80" }} /> },
    { to: "/salepage/renew", label: "ต่ออายุ / แพ็คเกจ", icon: <RefreshCw style={{ width: 16, height: 16, color: "#FF7A45" }} /> },
  ].filter((f) => f.show !== false);
  const menuLink: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: 10, padding: "11px 12px",
    borderRadius: 11, fontSize: 14, color: INK, cursor: "pointer",
  };

  const cardStyle = (active: boolean): React.CSSProperties => ({
    position: "relative", cursor: "pointer", borderRadius: 18, padding: "clamp(18px,2.2vw,22px)",
    background: "#fff", border: `1.5px solid ${active ? "#00B4A1" : "rgba(14,58,64,0.1)"}`,
    boxShadow: active ? "0 20px 40px -28px rgba(0,180,161,0.5)" : "none",
  });
  const featRow = (t: string) => (
    <div style={{ display: "flex", gap: 8, fontSize: 13.5, color: "#3C6367" }}>
      <Check style={{ width: 14, height: 14, color: "#00B4A1", flex: "0 0 auto", marginTop: 1 }} /> {t}
    </div>
  );

  const selMeta = PLAN_META[selectedPlan];
  const selAfter = `${selMeta.price}฿/เดือน`;

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "#F3F8F8", color: INK, fontFamily: FONT }}>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Anuphan:wght@400;500;600;700&family=IBM+Plex+Sans+Thai:wght@400;500;600&display=swap" rel="stylesheet" />
      </Helmet>

      {/* Header */}
      <header style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(243,248,248,0.9)", backdropFilter: "blur(18px)", borderBottom: "1px solid rgba(14,58,64,0.08)" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto", padding: "12px clamp(16px,3vw,32px)", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div onClick={() => navigate("/")} style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer" }}>
            <span style={{ width: 32, height: 32, borderRadius: 10, background: "linear-gradient(135deg, #00B4A1, #3A93B9)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontFamily: HEAD, fontWeight: 700, fontSize: 16, boxShadow: "0 8px 18px -8px rgba(0,180,161,0.55)" }}>S</span>
            <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 19, letterSpacing: "-0.01em" }}>SaleDi</span>
          </div>
          <nav style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 2, background: "oklch(0.96 0.016 175)", border: "1px solid oklch(0.915 0.022 175)", borderRadius: 999, padding: 4, position: "relative" }} ref={featRef}>
            <button type="button" onClick={() => navigate("/dashboard")} style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: FONT, fontSize: 14, color: "#3C6367", padding: "8px 15px", borderRadius: 999, border: 0, background: "transparent", cursor: "pointer" }}>
              <LayoutDashboard style={{ width: 15, height: 15 }} /> Dashboard
            </button>
            <button type="button" onClick={() => setFeatOpen((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: FONT, fontSize: 14, color: "#3C6367", padding: "8px 15px", borderRadius: 999, border: 0, background: "transparent", cursor: "pointer" }}>
              <Sparkles style={{ width: 15, height: 15, color: "#5B6FCB" }} /> ฟีเจอร์ของฉัน <ChevronDown style={{ width: 13, height: 13 }} />
            </button>
            {featOpen && (
              <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: 210, background: "#fff", border: "1px solid rgba(14,58,64,0.1)", borderRadius: 16, boxShadow: "0 26px 50px -22px rgba(14,58,64,0.4)", padding: 8, zIndex: 80 }}>
                {featureItems.map((f) => (
                  <div key={f.to} style={menuLink} onClick={() => { setFeatOpen(false); navigate(f.to); }}>{f.icon} {f.label}</div>
                ))}
              </div>
            )}
          </nav>
          <div style={{ display: "flex", alignItems: "center", gap: 10, position: "relative" }} ref={acctRef}>
            <span style={{ display: "flex", alignItems: "center", gap: 6, background: "#0E3A40", color: "#fff", fontSize: 12.5, fontWeight: 500, padding: "6px 13px", borderRadius: 999 }}>
              <Crown style={{ width: 13, height: 13, color: "#FF7A45" }} /> {planLabel(subscription?.plan)}
            </span>
            <button type="button" onClick={() => setAcctOpen((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 8, border: 0, background: "transparent", cursor: "pointer", padding: 0 }}>
              <span style={{ width: 38, height: 38, borderRadius: 999, background: "#EAF1F1", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <User style={{ width: 18, height: 18, color: "#3A93B9" }} />
              </span>
              <ChevronDown style={{ width: 15, height: 15, color: "#5B7D80" }} />
            </button>
            {acctOpen && (
              <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: 230, background: "#fff", border: "1px solid rgba(14,58,64,0.1)", borderRadius: 16, boxShadow: "0 26px 50px -22px rgba(14,58,64,0.4)", padding: 8, zIndex: 80 }}>
                <div style={{ padding: "10px 12px", borderBottom: "1px solid #EEF4F4", marginBottom: 6 }}>
                  <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 14, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{acctName}</div>
                  <div style={{ fontSize: 12, color: "#5B7D80", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.email || ""}</div>
                </div>
                <div style={menuLink} onClick={() => { setAcctOpen(false); navigate("/salepage/settings"); }}><Settings style={{ width: 16, height: 16 }} /> ตั้งค่า</div>
                <div style={{ ...menuLink, color: "#C0392B" }} onClick={() => { setAcctOpen(false); doLogout(); }}><LogOut style={{ width: 16, height: 16 }} /> ออกจากระบบ</div>
              </div>
            )}
          </div>
        </div>
      </header>

      {loading || salePageLoading ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ textAlign: "center" }}>
            <Loader style={{ width: 40, height: 40, color: "#00B4A1", margin: "0 auto 12px" }} className="animate-spin" />
            <p style={{ color: "#5B7D80" }}>กำลังโหลด...</p>
          </div>
        </div>
      ) : (
        <>
          <main style={{ flex: 1, maxWidth: 900, margin: "0 auto", width: "100%", padding: "clamp(22px,3vw,34px) clamp(16px,3vw,32px) clamp(110px,12vw,140px)", display: "flex", flexDirection: "column", gap: "clamp(18px,2.4vw,24px)" }}>
            <div>
              <h1 style={{ fontFamily: HEAD, fontWeight: 700, fontSize: "clamp(24px,2.6vw,30px)", margin: "0 0 4px", letterSpacing: "-0.02em" }}>ต่ออายุ / เปลี่ยนแพ็คเกจ</h1>
              <p style={{ fontSize: 14, color: "#5B7D80", margin: 0 }}>เลือกแพ็คเกจที่ใช่ แล้วยืนยันได้เลยในขั้นตอนเดียว</p>
            </div>

            {/* Current plan card */}
            <div style={{ background: "#fff", borderRadius: 22, padding: "clamp(18px,2.2vw,22px) clamp(20px,2.6vw,26px)", border: "1px solid rgba(14,58,64,0.06)", boxShadow: "0 24px 48px -40px rgba(14,58,64,0.3)", display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(0,180,161,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
                  <Package style={{ width: 19, height: 19, color: "#00B4A1" }} />
                </span>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 16.5 }}>{curLabel}</span>
                    {isProPlusYearly && (
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: "#0E7C86", background: "rgba(0,180,161,0.12)", padding: "3px 9px", borderRadius: 999 }}>ฟรีจาก Pro+ Yearly</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12.5, color: "#5B7D80", marginTop: 2 }}>{curSub}</div>
                </div>
              </div>
              <button type="button" onClick={() => setShowHistory(true)} style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", background: "transparent", border: 0, color: "#3A93B9", cursor: "pointer" }}>ดูประวัติการต่ออายุ</button>
            </div>

            {/* Admin-only: instantly set own plan for testing */}
            {isAdmin && (
              <div style={{ background: "#0E3A40", borderRadius: 18, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#fff", fontFamily: HEAD, fontWeight: 600, fontSize: 14 }}>
                  <Wrench style={{ width: 15, height: 15, color: "#FF7A45" }} /> Admin — ตั้งค่าแพ็คเกจตัวเองเพื่อทดสอบ
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {[
                    { key: "test", label: "ทดสอบระบบ", plan: "test" as const },
                    { key: "basic_monthly", label: "Basic รายเดือน", plan: "basic" as const, billing: "monthly" as const },
                    { key: "basic_yearly", label: "Basic รายปี", plan: "basic" as const, billing: "yearly" as const },
                    { key: "premium_monthly", label: "Premium รายเดือน", plan: "premium" as const, billing: "monthly" as const },
                    { key: "premium_yearly", label: "Premium รายปี", plan: "premium" as const, billing: "yearly" as const },
                    { key: "vip_monthly", label: "Premium VIP รายเดือน", plan: "premium_vip" as const, billing: "monthly" as const },
                    { key: "vip_yearly", label: "Premium VIP รายปี", plan: "premium_vip" as const, billing: "yearly" as const },
                  ].map((b) => (
                    <button
                      key={b.key}
                      type="button"
                      disabled={!!adminBusy}
                      onClick={() => adminSetPlan(b.plan, (b as any).billing)}
                      style={{
                        display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 500,
                        color: "#fff", background: adminBusy === b.key ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.18)", borderRadius: 999, padding: "8px 14px",
                        cursor: adminBusy ? "default" : "pointer", opacity: adminBusy && adminBusy !== b.key ? 0.5 : 1,
                      }}
                    >
                      {adminBusy === b.key && <Loader style={{ width: 12, height: 12 }} className="animate-spin" />}
                      {b.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Package selection */}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <span style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(91,111,203,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <LayoutGrid style={{ width: 16, height: 16, color: "#5B6FCB" }} />
                </span>
                <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 17.5 }}>เลือกแพ็คเกจ</span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,260px),1fr))", gap: 16 }}>
                {/* Basic */}
                <div onClick={() => setSelectedPlan("basic")} style={cardStyle(selectedPlan === "basic")}>
                  {selectedPlan === "basic" && (
                    <span style={{ position: "absolute", top: 16, right: 16, width: 22, height: 22, borderRadius: 999, background: "#00B4A1", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Check style={{ width: 13, height: 13, color: "#fff" }} />
                    </span>
                  )}
                  <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 18, marginBottom: 2 }}>Basic</div>
                  <div style={{ fontSize: 13, color: "#5B7D80", marginBottom: 14 }}>{PLAN_META.basic.desc}</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 5, marginBottom: 16 }}>
                    <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 28, color: "#00B4A1" }}>฿149</span>
                    <span style={{ fontSize: 13, color: "#5B7D80" }}>/ เดือน</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{PLAN_META.basic.base.map((t) => <div key={t}>{featRow(t)}</div>)}</div>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setBasicExpanded((v) => !v); }} style={{ marginTop: 12, background: "transparent", border: 0, padding: 0, fontSize: 12.5, color: "#3A93B9", cursor: "pointer", textAlign: "left" }}>{basicExpanded ? "ย่อรายการ" : "ดูฟีเจอร์ทั้งหมด"}</button>
                  {basicExpanded && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(14,58,64,0.08)" }}>{PLAN_META.basic.more.map((t) => <div key={t}>{featRow(t)}</div>)}</div>
                  )}
                </div>

                {/* Premium */}
                <div onClick={() => setSelectedPlan("premium")} style={cardStyle(selectedPlan === "premium")}>
                  <span style={{ position: "absolute", top: -11, left: 20, background: "linear-gradient(120deg, #FF7A45, #E85D2A)", color: "#fff", fontSize: 11, fontWeight: 600, padding: "4px 12px", borderRadius: 999 }}>แนะนำ</span>
                  {selectedPlan === "premium" && (
                    <span style={{ position: "absolute", top: 16, right: 16, width: 22, height: 22, borderRadius: 999, background: "#00B4A1", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Check style={{ width: 13, height: 13, color: "#fff" }} />
                    </span>
                  )}
                  <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 18, marginBottom: 2 }}>Premium</div>
                  <div style={{ fontSize: 13, color: "#5B7D80", marginBottom: 14 }}>{PLAN_META.premium.desc}</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 5, marginBottom: 16 }}>
                    <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 28, color: "#00B4A1" }}>฿249</span>
                    <span style={{ fontSize: 13, color: "#5B7D80" }}>/ เดือน</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{PLAN_META.premium.base.map((t) => <div key={t}>{featRow(t)}</div>)}</div>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setPremiumExpanded((v) => !v); }} style={{ marginTop: 12, background: "transparent", border: 0, padding: 0, fontSize: 12.5, color: "#3A93B9", cursor: "pointer", textAlign: "left" }}>{premiumExpanded ? "ย่อรายการ" : "ดูฟีเจอร์ทั้งหมด"}</button>
                  {premiumExpanded && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(14,58,64,0.08)" }}>{PLAN_META.premium.more.map((t) => <div key={t}>{featRow(t)}</div>)}</div>
                  )}
                </div>

                {/* Premium VIP */}
                <div onClick={() => setSelectedPlan("premium_vip")} style={cardStyle(selectedPlan === "premium_vip")}>
                  <span style={{ position: "absolute", top: -11, left: 20, background: "linear-gradient(120deg, #FF7A45, #FFB84D)", color: "#fff", fontSize: 11, fontWeight: 600, padding: "4px 12px", borderRadius: 999, display: "flex", alignItems: "center", gap: 4 }}><Crown style={{ width: 11, height: 11 }} /> AI</span>
                  {selectedPlan === "premium_vip" && (
                    <span style={{ position: "absolute", top: 16, right: 16, width: 22, height: 22, borderRadius: 999, background: "#00B4A1", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Check style={{ width: 13, height: 13, color: "#fff" }} />
                    </span>
                  )}
                  <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 18, marginBottom: 2 }}>Premium VIP</div>
                  <div style={{ fontSize: 13, color: "#5B7D80", marginBottom: 14 }}>{PLAN_META.premium_vip.desc}</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 5, marginBottom: 16 }}>
                    <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 28, color: "#00B4A1" }}>฿499</span>
                    <span style={{ fontSize: 13, color: "#5B7D80" }}>/ เดือน</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{PLAN_META.premium_vip.base.map((t) => <div key={t}>{featRow(t)}</div>)}</div>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setVipExpanded((v) => !v); }} style={{ marginTop: 12, background: "transparent", border: 0, padding: 0, fontSize: 12.5, color: "#3A93B9", cursor: "pointer", textAlign: "left" }}>{vipExpanded ? "ย่อรายการ" : "ดูฟีเจอร์ทั้งหมด"}</button>
                  {vipExpanded && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(14,58,64,0.08)" }}>{PLAN_META.premium_vip.more.map((t) => <div key={t}>{featRow(t)}</div>)}</div>
                  )}
                </div>
              </div>
            </div>

            {isProPlusYearly && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, background: "rgba(255,122,69,0.08)", borderRadius: 14, padding: "13px 16px", fontSize: 13, color: "#8A4A26" }}>
                <Info style={{ width: 15, height: 15, flex: "0 0 auto", marginTop: 1, color: "#FF7A45" }} />
                เมื่อ Pro+ Yearly หมดอายุ Salepage จะเปลี่ยนเป็นแพ็คเกจที่เลือกไว้และเริ่มเก็บค่าบริการอัตโนมัติ — หากต่ออายุ Pro+ Yearly ก่อนหมดอายุ จะได้ใช้ฟรีต่ออีก 1 ปี
              </div>
            )}
          </main>

          {/* Sticky confirm bar */}
          <div style={{ position: "sticky", bottom: 0, background: "rgba(255,255,255,0.94)", backdropFilter: "blur(14px)", borderTop: "1px solid rgba(14,58,64,0.08)", boxShadow: "0 -12px 30px -20px rgba(14,58,64,0.25)" }}>
            <div style={{ maxWidth: 900, margin: "0 auto", padding: "16px clamp(16px,3vw,32px)", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
              <div>
                <div style={{ fontSize: 12.5, color: "#5B7D80" }}>แพ็คเกจที่เลือก</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 19 }}>{selMeta.name}</span>
                  <span style={{ fontSize: 13, color: "#5B7D80" }}>{freeNow ? `ตอนนี้ฟรี · หลังหมดโปร ${selAfter}` : selAfter}</span>
                </div>
              </div>
              <button type="button" onClick={handleConfirm} style={{ display: "flex", alignItems: "center", gap: 9, background: "linear-gradient(120deg, #00B4A1, #3A93B9)", color: "#fff", border: 0, borderRadius: 999, padding: "14px 26px", fontFamily: HEAD, fontSize: 15.5, fontWeight: 600, cursor: "pointer", boxShadow: "0 16px 32px -16px rgba(0,180,161,0.5)" }}>
                {freeNow ? "ใช้แพ็คเกจนี้" : `ยืนยัน ${selMeta.name}`} <ArrowRight style={{ width: 16, height: 16 }} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* History modal */}
      {showHistory && (
        <div onClick={() => setShowHistory(false)} style={{ position: "fixed", inset: 0, background: "rgba(14,58,64,0.4)", backdropFilter: "blur(4px)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 22, padding: 24, width: "100%", maxWidth: 460, maxHeight: "80vh", overflowY: "auto", boxShadow: "0 40px 80px -30px rgba(14,58,64,0.5)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: HEAD, fontWeight: 700, fontSize: 17 }}>
                <Calendar style={{ width: 18, height: 18, color: "#00B4A1" }} /> ประวัติการต่ออายุ
              </div>
              <button type="button" onClick={() => setShowHistory(false)} style={{ background: "transparent", border: 0, cursor: "pointer", color: "#5B7D80" }}><X style={{ width: 20, height: 20 }} /></button>
            </div>
            {paymentHistory.length === 0 ? (
              <p style={{ textAlign: "center", color: "#5B7D80", padding: "24px 0", fontSize: 14 }}>ยังไม่มีประวัติการต่ออายุ</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {paymentHistory.map((p) => (
                  <div key={p.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 14, background: "#F3F8F8", borderRadius: 14 }}>
                    <div>
                      <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 14 }}>{p.plan === "premium" ? "Premium" : p.plan === "basic" ? "Basic" : "FREE"}</div>
                      <div style={{ fontSize: 12, color: "#5B7D80" }}>{formatDate(p.paidAt)}</div>
                      {p.amount === 0 && <div style={{ fontSize: 12, color: "#B45309", fontWeight: 600, marginTop: 2 }}>🎉 ฟรี (ส่วนลด 100%)</div>}
                    </div>
                    <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 14, color: p.amount === 0 ? "#B45309" : "#0E7C86" }}>{p.amount === 0 ? "ฟรี" : `฿${p.amount?.toLocaleString()}`}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
